import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonGroup, Menu, MenuItem } from '@mui/material';
import { ISelectOption } from 'components/general/types';
import { IFiniteStateMachine, IStateTransition, TRoutine } from 'components/general/types/cdh';
import { useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  EdgeProps,
  getBezierEdgeCenter,
  getBezierPath,
  useReactFlow,
  useStore,
} from 'react-flow-renderer';
import theme from 'theme';
import { RoutineContext, useRoutineMenuConfig } from '..';
import { selectedColor } from '../CustomNodes/styles';
import {
  disconnectFsmSubroutine,
  disconnectLogicalConfiguration,
  disconnectSchedule,
  disconnectStateTransition,
} from '../utils';
import useStyles from './styles';
import { getEdgeParams } from './utils';

const buttonSize = 30;

interface IEdgeButtonProps {
  x: number;
  y: number;
  size: number;
  onEdit?: (item?: string) => void;
  onDelete: (item?: string) => void;
  menuItems?: ISelectOption[];
  borderColor?: string;
}

const EdgeButtons = (props: IEdgeButtonProps) => {
  const { x, y, size, onEdit, onDelete, menuItems, borderColor } = props;
  const numButtons = onEdit ? 2 : 1;
  const { classes } = useStyles({ borderColor });
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const containerElement = document.getElementById('routines-diagram-container');
  return (
    <div>
      <ButtonGroup
        className={classes.edgeGroup}
        style={{
          position: 'absolute',
          zIndex: 5,
          top: y - (size * numButtons) / 2,
          left: x - size / 2,
        }}
        orientation="vertical"
      >
        {numButtons > 1 && (
          <Button
            id="edit"
            style={{
              padding: 3,
              minWidth: size,
              width: size,
              height: size,
              fontSize: size / 2,
            }}
            size="small"
            onClick={menuItems ? (e) => setAnchor(e.currentTarget) : () => onEdit && onEdit()}
          >
            <FontAwesomeIcon icon={faPen} />
          </Button>
        )}
        <Button
          id="delete"
          style={{
            padding: 3,
            minWidth: size,
            width: size,
            height: size,
            fontSize: size / 2,
          }}
          size="small"
          onClick={menuItems ? (e) => setAnchor(e.currentTarget) : () => onDelete()}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </ButtonGroup>
      {menuItems && (
        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          style={{ zIndex: 10 }}
          container={containerElement}
        >
          {menuItems.map(({ label, value }) => (
            <MenuItem
              key={value}
              onClick={() => {
                anchor?.id === 'delete' ? onDelete(value) : onEdit && onEdit(value);
                setAnchor(null);
              }}
            >
              {label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </div>
  );
};

const BaseEdge = (
  props: EdgeProps & {
    buttonProps?: {
      onEdit?: (item?: string) => void;
      onDelete: (item?: string) => void;
      menuItems?: ISelectOption[];
    };
    customPath?: { path: string; center: [number, number] };
  }
) => {
  const {
    id,
    sourceX,
    sourceY,
    source,
    targetX,
    targetY,
    target,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
    buttonProps,
    customPath,
  } = props;

  const { state, setState, canEdit } = useContext(RoutineContext);
  const { setEdges } = useReactFlow();
  const [menuConfig] = useRoutineMenuConfig();
  const _onDelete = useCallback(() => {
    setState((prev) => {
      const newState = {
        blocks: { ...prev.blocks },
        deletedBlocks: [...prev.deletedBlocks],
        dirtyBlocks: [...prev.dirtyBlocks],
      };
      const parent = newState.blocks[source] as TRoutine;
      if (parent.type === 'CombinationalLogic') {
        disconnectLogicalConfiguration(newState, id, { parent: source });
      }
      if (parent.type === 'FiniteStateMachine') {
        disconnectFsmSubroutine(newState, source, target);
      } else if (parent.type === 'RelativeSchedule' || parent.type === 'FixedSchedule') {
        disconnectSchedule(newState, source, target);
      } else {
        disconnectStateTransition(newState, id);
      }
      return newState;
    });
    setEdges((edges) => edges.filter((edge) => edge.id !== id));
  }, [id, source, target, setEdges, setState]);

  const d =
    customPath?.path ||
    getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });

  const [centerX, centerY] =
    customPath?.center ||
    getBezierEdgeCenter({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });

  const strokeColor = useMemo(() => {
    if (props.style?.stroke) return props.style.stroke;
    const sourceNode = state.blocks[source];
    if (sourceNode) {
      switch (sourceNode.type) {
        case 'CombinationalLogic':
          return theme.palette.primary.light;
        case 'FiniteStateMachine':
          return theme.palette.secondary.light;
        case 'FixedSchedule':
        case 'RelativeSchedule':
          return theme.palette.charts.primary.orange;
        default:
          return undefined;
      }
    }
    return undefined;
  }, [props.style, state.blocks, source]);

  // Hack to display non text labels - built in to reactflow v11 with <EdgeLabelRenderer/>
  const portalTarget = useStore((state) => state.domNode?.querySelector('.edgeLabelRenderer'));
  const transform = useStore((state) => state.transform);
  let buttons;
  if (canEdit && portalTarget && transform) {
    const [tx, ty, tScale] = transform;
    const { x, y } = { x: centerX * tScale + tx, y: centerY * tScale + ty };
    const size = buttonSize * tScale;
    buttons = createPortal(
      <EdgeButtons
        x={x}
        y={y}
        size={size}
        borderColor={
          menuConfig.ids.some((selectedId) =>
            buttonProps?.menuItems
              ? buttonProps.menuItems.some(({ value }) => value === selectedId)
              : id === selectedId
          )
            ? selectedColor
            : strokeColor
        }
        onDelete={_onDelete}
        {...buttonProps}
      />,
      portalTarget
    );
  }

  return (
    <>
      <g className="react-flow__connection">
        <path
          id={id}
          className="react-flow__edge-path"
          d={d}
          markerEnd={markerEnd}
          width={10}
          height={10}
          color={strokeColor}
          strokeWidth={2}
          style={{ ...style, stroke: strokeColor }}
        />
      </g>
      {buttons}
    </>
  );
};

export const LogicalConfigurationEdge = (props: EdgeProps) => {
  const { id } = props;
  const { setMenuConfig, deleteBlock } = useContext(RoutineContext);
  const { setEdges, setNodes } = useReactFlow();
  const editData = useCallback(() => {
    setMenuConfig({ open: true, action: 'edit', ids: [id] });
    setNodes((nodes) =>
      nodes.map((node) => ({ ...node, data: { ...node.data, isSelected: false } }))
    );
  }, [id, setMenuConfig, setNodes]);
  const deleteData = useCallback(() => {
    deleteBlock(id);
    setMenuConfig((prev) => (prev.ids.includes(id) ? { ...prev, open: false, ids: [] } : prev));
    setEdges((edges) => edges.filter((edge) => edge.id !== id));
  }, [id, deleteBlock, setMenuConfig, setEdges]);

  return <BaseEdge {...props} buttonProps={{ onEdit: editData, onDelete: deleteData }} />;
};

export const StateTransitionEdge = (props: EdgeProps) => {
  const { id, source, target } = props;
  const { menuConfig, state, setMenuConfig, deleteBlock } = useContext(RoutineContext);
  const { setEdges } = useReactFlow();

  const editData = useCallback(
    (item?: string) => setMenuConfig((prev) => ({ ...prev, ids: [prev.ids[0], item || id] })),
    [id, setMenuConfig]
  );
  const deleteData = useCallback(
    (item?: string) => {
      const useId = item || id;
      deleteBlock(useId);
      setEdges((edges) => edges.filter((edge) => edge.id !== useId));
    },
    [id, deleteBlock, setEdges]
  );

  const [twinEdges, cycle] = useMemo(() => {
    const parent = state.blocks[menuConfig.ids[0]] as IFiniteStateMachine;
    const twins: ISelectOption[] = [];
    let cycle = false;
    if (!parent?.transitions) return [undefined, cycle];

    parent.transitions.forEach((transitionId) => {
      const t = state.blocks[transitionId] as IStateTransition;
      if (t.fromState === source && t.toState === target) {
        twins.push({
          label: `${t.id}`,
          value: t.id,
        });
      } else if (t.fromState === target && t.toState === source) {
        cycle = true;
      }
    });

    return [twins.length > 1 ? twins : undefined, cycle];
  }, [menuConfig.ids, state, source, target]);

  const sourceNode = useStore(useCallback((store) => store.nodeInternals.get(source), [source]));
  const targetNode = useStore(useCallback((store) => store.nodeInternals.get(target), [target]));

  const { sx, sy, tx, ty } = getEdgeParams(sourceNode, targetNode);

  const customPath = useMemo(() => {
    // If the source and target are the same, create a loop
    if (sx === tx && sy === ty) {
      // create an svg path for a loop from a point to itself
      const x = sx;
      const y = sy;
      const path = `M ${x} ${y} C ${x + 50} ${y} ${x + 50} ${y + 100} ${x} ${y + 100} C ${x - 50} ${
        y + 100
      } ${x - 50} ${y} ${x} ${y}`;
      const center = [x, y + 100] as [number, number];
      return { path, center };
    }

    // If there is no cycle, create straight line svg path from source to target
    if (!cycle)
      return {
        path: `M ${sx} ${sy} L ${tx} ${ty}`,
        center: [0.5 * (sx + tx), 0.5 * (sy + ty)] as [number, number],
      };

    // Create an arc as a quadratic bezier curve
    const dx = tx - sx;
    const dy = ty - sy;
    const dist = Math.sqrt(dx * dx + dy * dy) / 100;
    const hx = sx + dx / 2 + dy / dist;
    const hy = sy + dy / 2 - dx / dist;
    const path = `M ${sx} ${sy} Q ${hx} ${hy} ${tx} ${ty}`;
    const center = [0.25 * sx + 0.5 * hx + 0.25 * tx, 0.25 * sy + 0.5 * hy + 0.25 * ty] as [
      number,
      number
    ];
    return { path, center };
  }, [sx, sy, tx, ty, cycle]);

  if (!sourceNode || !targetNode) {
    return null;
  }

  return (
    <BaseEdge
      {...props}
      buttonProps={{ onEdit: editData, onDelete: deleteData, menuItems: twinEdges }}
      style={{ stroke: theme.palette.secondary.light }}
      customPath={customPath}
    />
  );
};

export default BaseEdge;
